import { Draggable, Droppable } from "@hello-pangea/dnd";
import { fromZonedTime } from "date-fns-tz";
import { Formik } from "formik";
import React, { ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import * as Yup from "yup";
import {
  FilterDropDownValuesType,
  StandardReportPayload,
  TReportField,
  TReportFilter,
} from "../../api/types/Report";
import svgIcons from "../../assets/svg/svgIcons";
import { APP_CONFIG, REPORT_TYPES } from "../../constants/constants";
import useConversion from "../../hooks/conversion";
import {
  datepickerValueToUTC,
  timeInUTCToDatepickerValue,
  useAccountTimezone,
} from "../../hooks/timezone";
import {
  AGGREGATE_FUNCTION,
  PopupReportColumnAggregation,
} from "../../pages/reports/components/popupReportColumnAggregation";
import PopupReportColumnFilter, {
  ApplyReportColumnFilterCallback,
} from "../../pages/reports/components/popupReportColumnFilter";
import {
  DROPPABLE_ID_PREFIX_REPORT_COLUMN,
  REPORT_FIELD_OUTPUT_TYPES,
  REPORT_FIELD_OUTPUT_TYPES_WITH_AGGREGATION,
  REPORT_FILTER_OPERATOR_TYPES,
  REPORT_FILTER_TYPES,
} from "../../pages/reports/constants";
import { useCreateCustomReportContext } from "../../pages/reports/context/createCustomReport.context";
import {
  calculateGregorianDate,
  dateToGregorianDays,
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
} from "../../utils/dateTime";
import {
  AdminFormFieldInput,
  AdminFormFieldSubmit,
  TSelectboxOption,
} from "../admin/formFields";
import FTModal from "../common/FTModal";
import Pagination, { TPaginationSelected } from "../pagination";
import { ReportColorModeContext } from "./reportColorModeContext";
import ReportTableRow from "./reportTableRow";
import ReportTableRowGrandTotal from "./reportTableRowGrandTotal";
import AppSearchBox from "../searchBox";
import { useSelector } from "react-redux";

export type ReportTableIndexColumn = {
  header: string;
  id: string | undefined;
  identifier: string;
};

export type ReportTableColumn = ReportTableIndexColumn & {
  ordering?: boolean;
  show_selectable_values?: boolean;
  aggregation_applied?: boolean;
  field_output_type?: REPORT_FIELD_OUTPUT_TYPES;
  all_field_id?: string;
  field_object?: TReportField;
  convert_to_whole_number?: boolean;
};

interface ReportTableProps {
  customWrapperClass?: string;
  viewportHeightOffset?: "400" | "500" | "600" | null;
  viewportHeightOffsetClass?: string;
  isLoading: boolean;
  listingQueryParams: StandardReportPayload;
  customTableHeadStyles?: string;
  columns: ReportTableColumn[];
  data: (string | ReactNode)[] | undefined;
  page?: string | number;
  onNext?: () => void;
  onPrev?: () => void;
  count: number;
  gotoPage?: (page: number) => void;
  pageSize: number;
  setPageSize?: (size: number) => void;
  sorting?: string;
  setSorting?: (sorting: boolean) => void;
  filter?: string[];
  setFilter?: (filter: string[]) => void;
  hidePagination?: boolean;
  orderBy?: (arg0: string | undefined) => void;
  rawData: any;
  reportFilters: TReportFilter[];
  reportFields: TReportField[];
  reportType: string;
  // Color mode: dark theme, colored status badges, and every row in the range in one
  // scrolling list (no pager); onLoadMore fetches the next batch as the user nears the end.
  colorMode?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onApplyReportColumnFilter?: ApplyReportColumnFilterCallback;
  onApplyReportColumnCalc?: (
    fieldId: string,
    aggregate_function?: string,
    column_type?: number,
    value?: number,
    operator?: string
  ) => void;
}
const getBackendValueForEventSearch = (
  searchValue: string,
  fieldOutputType: REPORT_FIELD_OUTPUT_TYPES | undefined
): string => {
  if (fieldOutputType === REPORT_FIELD_OUTPUT_TYPES.STATUS_WITH_COLOR) {
    const lowerSearchValue = searchValue.toLowerCase();
    if (lowerSearchValue.includes("driving")) {
      return "In Motion||,Moving||";
    } else if (lowerSearchValue.includes("possible impact")) {
      return "Impact Event ALERT!!!!";
    } else if (lowerSearchValue.includes("heading change")) {
      return "Heading_Change";
    }
  }
  return searchValue;
};

const handleDateTimePickerValueOnChange = (
  column: ReportTableColumn,
  tzForDateFns: string,
  date: any
) => {
  let dateValueToSave = date;
  switch (column.field_output_type) {
    case REPORT_FIELD_OUTPUT_TYPES.GREGORIAN_DATE:
      const gregorianDays = dateToGregorianDays(date);
      dateValueToSave = gregorianDays;
      break;
    case REPORT_FIELD_OUTPUT_TYPES.TIME:
      // break if date is not of the form HH:mm:ss
      if (!date || date?.length !== 8) break;

      try {
        const dateStringInReportTimezone = `${new Date().toISOString().split("T")[0]}T${date}`;
        // convert dateStringInReportTimezone to UTC using date-fns
        const utc = Math.floor(
          datepickerValueToUTC(dateStringInReportTimezone, tzForDateFns)
        );
        const utcDateObject = new Date(utc * 1000);
        const utcIsoTimeChunk = utcDateObject
          .toISOString()
          .split("T")[1]
          .substring(0, 8);
        dateValueToSave = utcIsoTimeChunk;
      } catch (error) {
        if (APP_CONFIG.DEBUG.REPORTS) console.error("ERROR", error);
        dateValueToSave = "";
      }
      break;
    case REPORT_FIELD_OUTPUT_TYPES.DATE:
      dateValueToSave = formattedDateTimeBasedOnTimezone(
        date,
        tzForDateFns,
        APP_CONFIG.DATETIME_FORMATS.BACKEND.DATE
      );
      break;
    case REPORT_FIELD_OUTPUT_TYPES.DATE_TIME:
    default:
      dateValueToSave = datepickerValueToUTC(date, tzForDateFns);
      break;
  }
  return dateValueToSave;
};

const ReportTable = (props: ReportTableProps) => {
  const {
    customWrapperClass = "relative",
    viewportHeightOffset = null,
    viewportHeightOffsetClass = "",
    columns,
    data,
    isLoading = false,
    customTableHeadStyles = "",
    hidePagination = false,
    count,
    listingQueryParams,
    rawData,
    reportFilters,
    reportFields,
    reportType,
    onApplyReportColumnFilter,
    onApplyReportColumnCalc,
    colorMode = false,
    onLoadMore,
    hasMore = false,
    isLoadingMore = false,
  } = props;
  const { t } = useTranslation();
  const { t: tReports } = useTranslation("translation", {
    keyPrefix: "reports",
  });
  const navigate = useNavigate();
  let wrapperHeightClass = "";
  const userIsImpersonated = useSelector(
    (state: { commonReducer: { userIsImpersonated: boolean } }) =>
      state.commonReducer.userIsImpersonated
  );
  switch (viewportHeightOffset) {
    case "400":
      // wrapperHeightClass = 'max-h-[calc(100vh-400px)]';
      wrapperHeightClass = userIsImpersonated
        ? "max-h-[calc(100vh-420px)]" // + 40px for impersonation (blue) bar
        : "max-h-[calc(100vh-380px)]"; // 48+32+52+16+40+42+24+16+16+38+28= 352 ~= 360 + 10 (10px of bottom spacing) = 370
      break;
    case "500":
      wrapperHeightClass = "max-h-[calc(100vh-500px)]"; // TODO: check later if this is being used
      break;
    case "600":
      wrapperHeightClass = "max-h-[calc(100vh-600px)]"; // TODO: check later if this is being used
      break;
    default:
      wrapperHeightClass = `${viewportHeightOffsetClass} min-h-report-table`; // extra height added to display column filter popup
  }

  const { tzForDateFns } = useAccountTimezone();

  const [hoveringColumnFiltersRow, setHoveringColumnFiltersRow] =
    useState<boolean>(false);

  const {
    columnsSearchKeyword,
    setColumnsSearchKeyword,
    columnsOperator,
    setColumnsOperator,
    skipRunReport,
    setSkipRunReport,
    specificReportResponse,
    columnsVisibility,
  } = useCreateCustomReportContext();
  // serialized once per render (it is part of every row's key)
  const columnsSearchKeywordKey = JSON.stringify(columnsSearchKeyword);
  // state to store column popup visibility
  const [showColumnsPopup, setShowColumnsPopup] = useState<
    Record<string, boolean>
  >({});
  const [showBetweenDatePopupForColumn, setShowBetweenDatePopupForColumn] =
    useState<string | boolean>(false);
  const [showBetweenNumberPopupForColumn, setShowBetweenNumberPopupForColumn] =
    useState<string | boolean>(false);
  const { convertToUnit } = useConversion();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const datepickerRefs = React.useRef<Array<any | null>>([]);
  const handlePopup = (column?: ReportTableColumn, index?: number) => {
    const isDatePopup = [
      REPORT_FIELD_OUTPUT_TYPES.DATE_TIME,
      REPORT_FIELD_OUTPUT_TYPES.DATE,
      REPORT_FIELD_OUTPUT_TYPES.TIME,
      REPORT_FIELD_OUTPUT_TYPES.GREGORIAN_DATE,
    ].includes(column?.field_output_type as any as REPORT_FIELD_OUTPUT_TYPES);

    // Close the column popup first
    if (index !== undefined)
      setShowColumnsPopup((prev) => ({
        ...prev,
        [String(index)]: false,
      }));

    // Open the appropriate popup
    if (isDatePopup) {
      setShowBetweenDatePopupForColumn(column?.id || false);
    } else {
      setShowBetweenNumberPopupForColumn(column?.id || false);
    }
    // Focus the input field for the given index
    if (index !== undefined && inputRefs.current) {
      const ref = inputRefs.current[index];
      const datepickerRef = datepickerRefs.current[index];
      if (ref instanceof HTMLInputElement) {
        ref.focus();
        // Open the calendar
        if (datepickerRef?.open) {
          datepickerRef.open();
        } else {
          console.error(
            `Failed to open calendar: Invalid DatePicker ref at index ${index}`,
            datepickerRef
          );
        }
      } else {
        console.error(
          `Failed to focus: Invalid ref at index ${index}, got`,
          ref
        );
      }
    } else {
      console.error(`Invalid index or refs at index ${index}`);
    }
  };

  // header popup filter logic starts here
  const ownerOrganization = localStorage.getItem("ownerOrganization");

  // dropdown values for each column
  // let filterDropDownValues: FilterDropDownValuesType = {};
  // selected dropdown values for each column, object keys will be COLUMN_DROPDOWN_TYPES and values will be array of objects having id and description
  const [selectedDropDownValues, setSelectedDropDownValues] =
    useState<FilterDropDownValuesType>({});

  // const [dataHeaderFiltered, setDataHeaderFiltered] = useState<any[]>([]);
  // console.log("dataHeaderFiltered", dataHeaderFiltered);

  //filteration logic based on selected filter
  // useEffect(() => {
  //   if (data) {
  //     let updatedFilterData = [...data];
  //     if (Object.keys(selectedDropDownValues).length > 0) {
  //       Object.keys(selectedDropDownValues).forEach((key) => {
  //         //index of the element in data array where dropdown value matches with the selected dropdown value
  //         const indexOFSelectedDropDown = columns.findIndex(
  //           (column) => column?.field_output_type?.toString() === key
  //         );
  //         if (indexOFSelectedDropDown !== -1) {
  //           //ids of selected dropdown values to match with data
  //           const selectedIds = selectedDropDownValues[key].map(
  //             (item) => item.id
  //           );
  //           // filteration based on above selected id
  //           updatedFilterData = updatedFilterData.filter((row) => {
  //             return selectedIds.includes(row[indexOFSelectedDropDown]);
  //           });
  //         }
  //       });
  //     }
  //     setDataHeaderFiltered(updatedFilterData);
  //   }
  // }, [selectedDropDownValues, data, columns]);

  // updating the state here when filter's apply button is clicked
  const handleApplyReportColumnFilter = (
    identifier: string,
    fieldOutputType: REPORT_FIELD_OUTPUT_TYPES,
    selectedValues: string[]
  ) => {
    console.log(
      "[handleApplyReportColumnFilter] identifier:",
      identifier,
      "fieldOutputType:",
      fieldOutputType,
      "selectedValues:",
      selectedValues
    );
    onApplyReportColumnFilter?.(identifier, fieldOutputType, selectedValues);
    setSelectedDropDownValues((prevState: any) => {
      console.log("prevState", prevState);
      const newState = { ...prevState };
      newState[fieldOutputType] = selectedValues;
      console.log("newState", newState);
      return newState;
    });
  };

  // header popup filter ends here

  const wrapperRef = useRef<HTMLDivElement>(null);
  const { getUnitByOutputType, convertValueForAPI } = useConversion();
  const wrapperClass = `${customWrapperClass} ${wrapperHeightClass}${colorMode ? " report-color-mode" : ""}`;

  // Color mode renders only the rows in view (plus overscan) between two spacer rows, so a
  // range of tens of thousands of rows stays responsive. Rows are single-line and the same
  // height; reports with group-by (expandable, variable-height rows) render every row.
  const hasGroupBy = !!specificReportResponse?.report_fields?.some(
    (field) => field.is_group_by
  );
  const windowRows = colorMode && !hasGroupBy;
  const rowCount = data?.length || 0;
  const [rowHeight, setRowHeight] = useState(49);
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 100 });
  const tbodyRef = useRef<HTMLTableSectionElement>(null);

  const updateVisibleRange = () => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const tbodyTop = tbodyRef.current?.offsetTop ?? 0;
    const overscan = 30;
    const first = Math.floor(Math.max(0, wrapper.scrollTop - tbodyTop) / rowHeight);
    const visible = Math.ceil(wrapper.clientHeight / rowHeight);
    const start = Math.max(0, first - overscan);
    const end = Math.min(rowCount, first + visible + overscan);
    setVisibleRange((prev) =>
      prev.start === start && prev.end === end ? prev : { start, end }
    );
    // Fetch the next batch when the last loaded row is within a couple of screens.
    if (
      colorMode &&
      hasMore &&
      !isLoadingMore &&
      !isLoading &&
      first + visible * 3 >= rowCount
    ) {
      onLoadMore?.();
    }
  };

  // measure a rendered row once, so spacer heights match the real rows
  useEffect(() => {
    if (!windowRows) return;
    const firstRow = tbodyRef.current?.querySelector<HTMLTableRowElement>(
      "tr[data-report-row]"
    );
    if (firstRow && firstRow.offsetHeight > 0 && firstRow.offsetHeight !== rowHeight) {
      setRowHeight(firstRow.offsetHeight);
    }
  }, [windowRows, rowHeight, rowCount, visibleRange.start]);

  // re-evaluate after new rows arrive (also loads more if they don't fill the screen yet)
  useEffect(() => {
    if (colorMode) updateVisibleRange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colorMode, rowCount, rowHeight, hasMore, isLoadingMore, isLoading]);

  /**
   * Method to handle sorting of the table
   * Column not selected yet -> apply descending sorting
   * Column selected -> apply ascending sorting
   * Column selected and already in ascending -> reset sorting
   * @param column Column of the table
   */
  const onSortClick = (column: ReportTableColumn) => {
    if (column.ordering && props?.orderBy) {
      if (
        listingQueryParams.ordering !== column.id &&
        listingQueryParams.ordering !== `-${column.id}`
      ) {
        props.orderBy(`-${column.id}`);
      } else if (listingQueryParams.ordering === `-${column.id}`) {
        props.orderBy(column.id);
      } else {
        props.orderBy(undefined);
      }
    }
  };

  //state to store calculation popup visibility
  const [showSummationPopups, setShowSummationPopups] = useState<
    Record<string, boolean>
  >({});
  const toggleSummationPopup = (idx: string) => {
    setShowSummationPopups((prev) => ({
      ...prev,
      [idx]: !prev[idx],
    }));
  };
  useEffect(() => {
    const initialSummationPopupVisibility: Record<string, boolean> = {};
    columns.forEach((_, idx) => {
      initialSummationPopupVisibility[String(idx)] = false;
    });
    setShowSummationPopups(initialSummationPopupVisibility);
  }, [columns]);
  const aSummationPopupIsOpen = Object.values(showSummationPopups).some(
    (value) => value === true
  );

  useEffect(() => {
    const initialColumnsPopupVisibility: Record<string, boolean> = {};
    columns.map((column, idx) => {
      // initialize all columns popup visibility to false
      return (initialColumnsPopupVisibility[String(idx)] = false);
    });
    setShowColumnsPopup(initialColumnsPopupVisibility);
  }, [columns, selectedDropDownValues]);
  const aPopupIsOpen = Object.values(showColumnsPopup).some(
    (value) => value === true
  );

  // toggles display of icons in the header row
  useEffect(() => {
    if (aPopupIsOpen) {
      setHoveringColumnFiltersRow(true);
    } else {
      setHoveringColumnFiltersRow(false);
    }
  }, [showColumnsPopup]);

  // debounced search keyword for columns
  const debouncedSetColumnsSearchKeyword = useDebouncedCallback(
    (value: string, columnId: string) => {
      // hold the API call unless we change page as well
      setSkipRunReport?.(true);
      // set search keyword for each column
      setColumnsSearchKeyword?.((prev) => ({
        ...prev,
        [columnId]: value,
      }));
      // skipRunReport is set to false after page is set to 1 in singleReport.tsx
    },
    500
  );

  const BetweenNumberPopup = (column: ReportTableColumn, index: number) => {
    const appliedValue =
      columnsSearchKeyword?.[column?.id as string]?.split(",");
    const apppliedRangeStart = convertToUnit(
      column?.field_output_type as REPORT_FIELD_OUTPUT_TYPES,
      appliedValue?.[0] || undefined,
      column?.convert_to_whole_number
    )?.finalData as string;
    const apppliedRangeEnd = convertToUnit(
      column?.field_output_type as REPORT_FIELD_OUTPUT_TYPES,
      appliedValue?.[1] || undefined,
      column?.convert_to_whole_number
    )?.finalData as string;

    return (
      <FTModal
        positionHorizontal="center"
        visible={showBetweenNumberPopupForColumn === column.id}
        heading={column.header}
        onHide={() => setShowBetweenNumberPopupForColumn(false)}
        position="center"
        containerStyle="max-w-[calc(100vw-4rem)] w-[560px]"
      >
        <Formik
          initialValues={{
            range_start: apppliedRangeStart || "",
            range_end: apppliedRangeEnd || "",
          }}
          validationSchema={Yup.object().shape({
            range_start: Yup.number().required("Start value is required"),

            range_end: Yup.number()
              .required("End value is required")
              .min(
                Yup.ref("range_start"),
                "End value must be greater than or equal to Start value"
              ),
          })}
          onSubmit={(values, { setSubmitting }) => {
            const { range_start, range_end } = values;
            const range_start_api = String(
              convertValueForAPI(column.field_output_type, String(range_start))
            );
            const range_end_api = String(
              convertValueForAPI(column.field_output_type, String(range_end))
            );
            if (range_start !== "" && range_end !== "") {
              debouncedSetColumnsSearchKeyword(
                `${range_start_api},${range_end_api}`,
                String(column.id)
              );
              setShowBetweenNumberPopupForColumn(false);
            }
          }}
        >
          {({
            handleSubmit,
            setFieldValue,
            validateForm,
            values,
            isValid,
            dirty,
            errors,
            touched,
          }) => (
            <form className="flex flex-col w-full" onSubmit={handleSubmit}>
              <div className="flex justify-between text-left">
                {/* Start Value Input */}
                <div className="flex flex-1 mx-2">
                  <AdminFormFieldInput
                    customWrapperClass="z-10 flex-1"
                    innerWrapperClass="mt-0 relative flex w-full"
                    basicInputClass="w-full h-11 rounded-md text-xs shadow-sm pe-10 border border-gray-300"
                    label={"Start Value"}
                    id={`range_start`}
                    name={`range_start`}
                    type={"number"}
                    placeholder="Enter start value"
                    value={values.range_start}
                    error={errors.range_start}
                    touched={touched.range_start}
                    onChange={(e) => {
                      setFieldValue("range_start", e.target.value);
                      setTimeout(() => {
                        validateForm();
                      }, 100);
                    }}
                  />
                </div>
                {/* End Value Input */}
                <div className="flex flex-1 mx-2">
                  <AdminFormFieldInput
                    customWrapperClass="z-10 flex-1"
                    innerWrapperClass="mt-0 relative flex w-full"
                    basicInputClass="w-full h-11 rounded-md text-xs shadow-sm pe-10 border border-gray-300"
                    label={"End Value"}
                    id={`range_end`}
                    name={`range_end`}
                    type={"number"}
                    placeholder="Enter end value"
                    value={values.range_end}
                    error={errors.range_end}
                    touched={touched.range_end}
                    onChange={(e) => {
                      setFieldValue("range_end", e.target.value);
                      setTimeout(() => {
                        validateForm();
                      }, 100);
                    }}
                  />
                </div>
              </div>
              <div className="flex justify-center mt-10">
                <AdminFormFieldSubmit
                  type="submit"
                  variant="primary-like"
                  customWrapperClass="w-52"
                  label={<span>{t("confirm")}</span>}
                  titleText={t("confirm")}
                  disabled={!isValid || !dirty}
                />
              </div>
            </form>
          )}
        </Formik>
      </FTModal>
    );
  };

  const BetweenDatePopup = (column: ReportTableColumn, index: number) => {
    return (
      <FTModal
        positionHorizontal="center"
        visible={showBetweenDatePopupForColumn === column.id}
        heading={column.header}
        onHide={() => setShowBetweenDatePopupForColumn(false)}
        position="center"
        containerStyle="max-w-[calc(100vw-4rem)] w-[560px]"
      >
        <Formik
          initialValues={{
            datetime_picker_start:
              column.field_output_type ===
              REPORT_FIELD_OUTPUT_TYPES.GREGORIAN_DATE
                ? // gregorian date today in gregorian days minus 1
                  `${dateToGregorianDays(
                    new Date(new Date().getTime() - 86400000)
                      .toISOString()
                      .split("T")[0]
                  )}`
                : null,
            datetime_picker_end:
              column.field_output_type ===
              REPORT_FIELD_OUTPUT_TYPES.GREGORIAN_DATE
                ? // gregorian date
                  `${dateToGregorianDays(new Date().toISOString().split("T")[0])}`
                : null,
          }}
          validationSchema={Yup.object().shape({
            datetime_picker_start: Yup.string().required(
              "Start date/time is required"
            ),
            datetime_picker_end: Yup.string().required(
              "End date/time is required"
            ),
          })}
          onSubmit={(values, { setSubmitting }) => {
            const start = values.datetime_picker_start || "";
            const end = values.datetime_picker_end || "";

            if (!!start && !!end) {
              let value_start: any = start;
              let value_end: any = end;
              switch (column.field_output_type) {
                case REPORT_FIELD_OUTPUT_TYPES.GREGORIAN_DATE:
                  // value_start = dateToGregorianDays(start);
                  // value_end = dateToGregorianDays(end);
                  break;
                case REPORT_FIELD_OUTPUT_TYPES.DATE:
                  value_start = formattedDateTimeBasedOnTimezone(
                    start,
                    tzForDateFns,
                    APP_CONFIG.DATETIME_FORMATS.BACKEND.DATE
                  );
                  value_end = formattedDateTimeBasedOnTimezone(
                    end,
                    tzForDateFns,
                    APP_CONFIG.DATETIME_FORMATS.BACKEND.DATE
                  );
                  break;
                case REPORT_FIELD_OUTPUT_TYPES.TIME:
                  // no conversion is needed for time
                  break;
                case REPORT_FIELD_OUTPUT_TYPES.DATE_TIME:
                default:
                  // no conversion is needed for datetime, thus disabled below code
                  // value_start = datepickerValueToUTC(start, tzForDateFns);
                  // value_end = datepickerValueToUTC(end, tzForDateFns);
                  break;
              }
              debouncedSetColumnsSearchKeyword(
                `${value_start},${value_end}`,
                String(column.id)
              );
              setShowBetweenDatePopupForColumn(false);
            }
          }}
        >
          {({
            handleSubmit,
            setFieldValue,
            validateForm,
            values,
            isValid,
            dirty,
          }) => {
            return (
              <form className="flex flex-col w-full" onSubmit={handleSubmit}>
                {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
                  <pre className="text-[8px] text-left">
                    {JSON.stringify(values, null, 2)}
                    <br />
                    {calculateGregorianDate(
                      Number(values?.datetime_picker_start)
                    )}
                    <br />
                    {calculateGregorianDate(
                      Number(values?.datetime_picker_end)
                    )}
                  </pre>
                )}
                <div className="flex justify-between text-left">
                  <div className="flex flex-1 mx-2">
                    <AdminFormFieldInput
                      customWrapperClass="date-picker z-10 flex-1  [&>label]:text-left"
                      innerWrapperClass="mt-0 relative flex w-full"
                      basicInputClass="w-full h-11 rounded-tl-md rounded-bl-md text-xs shadow-sm pe-10"
                      label={"Start Date"}
                      type={
                        column.field_output_type ===
                          REPORT_FIELD_OUTPUT_TYPES.DATE ||
                        column.field_output_type ===
                          REPORT_FIELD_OUTPUT_TYPES.GREGORIAN_DATE
                          ? "date-picker"
                          : column.field_output_type ===
                              REPORT_FIELD_OUTPUT_TYPES.TIME
                            ? "time-picker"
                            : "datetime-picker"
                      }
                      id={`datetime_picker_start`}
                      name={`datetime_picker_start`}
                      // restrict only when field_output_type is not gregorian date
                      {...(column.field_output_type !==
                        REPORT_FIELD_OUTPUT_TYPES.GREGORIAN_DATE && {
                        maxTime: new Date(),
                      })}
                      value={
                        column.field_output_type ===
                          REPORT_FIELD_OUTPUT_TYPES.TIME &&
                        !!values?.datetime_picker_start
                          ? `${new Date().toISOString().split("T")[0]}T${formattedDateTimeBasedOnTimezone(
                              fromZonedTime(
                                `${new Date().toISOString().split("T")[0]}T${values?.datetime_picker_start}`,
                                "GMT" // tzForDateFns
                              ),
                              tzForDateFns,
                              APP_CONFIG.DATETIME_FORMATS.BACKEND.TIME
                            )}`
                          : column.field_output_type ===
                              REPORT_FIELD_OUTPUT_TYPES.GREGORIAN_DATE
                            ? // gregorian date
                              calculateGregorianDate(
                                Number(values?.datetime_picker_start)
                              )
                            : column.field_output_type ===
                                  REPORT_FIELD_OUTPUT_TYPES.DATE_TIME &&
                                !!values?.datetime_picker_start
                              ? timeInUTCToDatepickerValue(
                                  Number(values?.datetime_picker_start),
                                  tzForDateFns
                                )
                              : values?.datetime_picker_start
                      }
                      onDatepickerChange={(fieldName: string, value: any) => {
                        const dateValueToSave =
                          handleDateTimePickerValueOnChange(
                            column,
                            tzForDateFns,
                            value
                          );
                        if (
                          column.field_output_type ===
                          REPORT_FIELD_OUTPUT_TYPES.TIME
                        ) {
                          if (dateValueToSave?.length === 8) {
                            setFieldValue(fieldName, dateValueToSave);
                          }
                        } else {
                          setFieldValue(fieldName, dateValueToSave);
                        }
                        if (
                          column.field_output_type ===
                          REPORT_FIELD_OUTPUT_TYPES.TIME
                        )
                          return;
                        // prefill end time with start time + 1 day
                        const selectedDate = new Date(value);
                        const utcDate = fromZonedTime(
                          selectedDate,
                          tzForDateFns
                        );
                        const jsTime = new Date(utcDate).getTime();
                        const endDate = new Date(jsTime);
                        endDate.setDate(endDate.getDate() + 1);
                        let dateValueToSaveEnd =
                          handleDateTimePickerValueOnChange(
                            column,
                            tzForDateFns,
                            endDate
                          );
                        setFieldValue(
                          "datetime_picker_end",
                          column.field_output_type ===
                            REPORT_FIELD_OUTPUT_TYPES.DATE_TIME && endDate
                            ? datepickerValueToUTC(
                                `${endDate.toISOString().split("T")[0]}T23:59:59`,
                                tzForDateFns
                              )
                            : dateValueToSaveEnd
                        );
                        setTimeout(() => {
                          validateForm();
                        }, 100);
                      }}
                    />
                  </div>
                  <div className="flex flex-1 mx-2">
                    <AdminFormFieldInput
                      customWrapperClass="date-picker z-10 flex-1"
                      innerWrapperClass="mt-0 relative flex w-full"
                      basicInputClass="w-full h-11 rounded-tl-md rounded-bl-md text-xs shadow-sm pe-10"
                      label={"End Date"}
                      type={
                        column.field_output_type ===
                          REPORT_FIELD_OUTPUT_TYPES.DATE ||
                        column.field_output_type ===
                          REPORT_FIELD_OUTPUT_TYPES.GREGORIAN_DATE
                          ? "date-picker"
                          : column.field_output_type ===
                              REPORT_FIELD_OUTPUT_TYPES.TIME
                            ? "time-picker"
                            : "datetime-picker"
                      }
                      id={`datetime_picker_end`}
                      name={`datetime_picker_end`}
                      value={
                        column.field_output_type ===
                          REPORT_FIELD_OUTPUT_TYPES.TIME &&
                        !!values?.datetime_picker_end
                          ? `${new Date().toISOString().split("T")[0]}T${formattedDateTimeBasedOnTimezone(
                              fromZonedTime(
                                `${new Date().toISOString().split("T")[0]}T${values?.datetime_picker_end}`,
                                "GMT" // tzForDateFns
                              ),
                              tzForDateFns,
                              APP_CONFIG.DATETIME_FORMATS.BACKEND.TIME
                            )}`
                          : column.field_output_type ===
                              REPORT_FIELD_OUTPUT_TYPES.GREGORIAN_DATE
                            ? // gregorian date
                              calculateGregorianDate(
                                Number(values?.datetime_picker_end)
                              )
                            : column.field_output_type ===
                                  REPORT_FIELD_OUTPUT_TYPES.DATE_TIME &&
                                !!values?.datetime_picker_end
                              ? timeInUTCToDatepickerValue(
                                  Number(values?.datetime_picker_end),
                                  tzForDateFns
                                )
                              : values?.datetime_picker_end
                      }
                      // restrict only when field_output_type is not gregorian date
                      {...(column.field_output_type ===
                      REPORT_FIELD_OUTPUT_TYPES.GREGORIAN_DATE
                        ? {
                            minTime: !!values?.datetime_picker_start
                              ? new Date(
                                  calculateGregorianDate(
                                    Number(values?.datetime_picker_end)
                                  )
                                )
                              : undefined,
                          }
                        : {
                            maxTime: new Date(),
                            minTime: !!values?.datetime_picker_start
                              ? new Date(values?.datetime_picker_start)
                              : new Date(),
                          })}
                      onDatepickerChange={(fieldName: string, value: any) => {
                        const dateValueToSave =
                          handleDateTimePickerValueOnChange(
                            column,
                            tzForDateFns,
                            value
                          );
                        if (
                          column.field_output_type ===
                            REPORT_FIELD_OUTPUT_TYPES.TIME &&
                          dateValueToSave?.length !== 8
                        )
                          return;
                        setFieldValue(fieldName, dateValueToSave);
                      }}
                    />
                  </div>
                </div>
                <div className="flex justify-center mt-10">
                  <AdminFormFieldSubmit
                    type="submit"
                    variant="primary-like"
                    customWrapperClass="w-52"
                    label={<span>{t("confirm")}</span>}
                    titleText={t("confirm")}
                    disabled={!isValid || !dirty}
                  />
                </div>
              </form>
            );
          }}
        </Formik>
      </FTModal>
    );
  };

  const showTbody =
    // not standard report
    reportType !== REPORT_TYPES.STANDARD &&
    // has aggregation
    specificReportResponse?.report_fields.find(
      (field) => !!field.aggregate_function
    ) &&
    // no group by
    specificReportResponse?.report_fields.filter((field) => field.is_group_by)
      .length === 0
      ? false // hide tbody
      : true; // show tbody

  return (
    <>
      {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
        <div className="Xabsolute flex gap-4 -mt-4 text-[8px] z-10">
          <pre className="">
            columnsSearchKeyword: ({Object.keys(columnsSearchKeyword)?.length})
            <br />
            {JSON.stringify(columnsSearchKeyword, null, 2)}
          </pre>
          <pre className="">
            columnsOperator: ({Object.keys(columnsOperator)?.length})
            <br />
            {JSON.stringify(columnsOperator, null, 2)}
          </pre>
          <pre className="">skipRunReport: {skipRunReport?.toString()}</pre>
        </div>
      )}
      <ReportColorModeContext.Provider value={colorMode}>
      <div
        className={`${wrapperClass}${aPopupIsOpen ? " overflow-hidden" : " overflow-auto"}`}
        ref={wrapperRef}
        onScroll={colorMode ? updateVisibleRange : undefined}
      >
        {/* {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
          <div className="flex gap-2 absolute bottom-0 right-0 text-[8px] text-red-400 bg-white/90 h-60">
            <pre className="relative overflow-auto">
              <span className="sticky top-0">columns:</span>
              <br />
              {JSON.stringify(columns, null, 2)}
            </pre>
            <pre className="relative overflow-auto">
              <span className="sticky top-0">selectedDropDownValues:</span>
              <br />
              {JSON.stringify(selectedDropDownValues, null, 2)}
            </pre>
          </div>
        )} */}
        <div
          className={`sticky top-1/2 left-0 z-report-table-loading-overlay min-w-full h-full${isLoading ? " " : " hidden"}`}
        >
          <div className="report-loading-overlay absolute min-h-report-table min-w-full top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white/75 flex items-center justify-center">
            <img
              src={svgIcons.LoadingAnimation}
              alt="loading-animation"
              className="size-16"
            />
          </div>
        </div>
        <table
          className={`w-full transition border-collapse border-spacing-0 Xmin-h-report-table`}
        >
          <tfoot className="sticky z-report-table-tfoot bottom-0 bg-white border-t border-gray-500">
            {/* Aggregation - Grand Total Row */}
            {!!specificReportResponse?.report_fields?.find(
              (field) => !!field.aggregate_function
            ) && (
              <ReportTableRowGrandTotal
                // key moved here from <ReportTable /> to <ReportTableRowGrandTotal /> to reset the row on column search
                key={`report_table_row_grandtotal_${JSON.stringify(columnsSearchKeyword)}`}
              />
            )}

            {/* Pagination */}
            <tr>
              <td colSpan={columns.length}>
                <div className="sticky left-0 w-report-table-no-items">
                  {colorMode ? (
                    <div className="report-color-mode-status px-4 py-2 text-sm font-medium">
                      {isLoadingMore
                        ? tReports("color_mode.loading_more")
                        : hasMore
                          ? tReports("color_mode.rows_loaded", {
                              loaded: rowCount.toLocaleString(),
                              total: count.toLocaleString(),
                            })
                          : tReports("color_mode.all_rows_loaded", {
                              total: rowCount.toLocaleString(),
                            })}
                    </div>
                  ) : hidePagination ? null : (
                    <Pagination
                      pageSize={listingQueryParams.page_size}
                      handlePageSizeChange={(e: TSelectboxOption | null) => {
                        props.setPageSize &&
                          props.setPageSize(parseInt(`${e?.value}`));
                      }}
                      totalPages={
                        count
                          ? Math.ceil(count / listingQueryParams.page_size)
                          : 1
                      }
                      forcePage={listingQueryParams.page - 1}
                      handlePageClick={(data: TPaginationSelected) => {
                        props.gotoPage && props.gotoPage(data?.selected + 1);
                        if (!!wrapperRef.current) {
                          wrapperRef.current.scrollTo({
                            behavior: "smooth",
                            top: 0,
                          }); // scroll to top of the table
                        }
                      }}
                      paginCurrentPage={listingQueryParams.page}
                      paginItemsOnCurrentPage={listingQueryParams.page_size}
                      // paginItemsOnCurrentPage={data?.length || 0} // Commenting this as it is causing UI bug
                      paginTotalItemsCount={count}
                      isLoading={isLoading}
                    />
                  )}
                </div>
              </td>
            </tr>
          </tfoot>
          {/* element layering behaves differently for tables, thus... */}
          {/* tbody & tfoot moved before thead to let column popup overlap over 'no items found' sticky element */}
          {showTbody && (
            <tbody
              ref={tbodyRef}
              className="z-report-table-tbody bg-white divide-y divide-gray-200 border-b border-gray-200"
            >
              {!!data && data.length > 0 ? (
                <>
                  {windowRows && visibleRange.start > 0 && (
                    <tr
                      aria-hidden="true"
                      style={{ height: visibleRange.start * rowHeight }}
                    />
                  )}
                  {(windowRows
                    ? data.slice(visibleRange.start, visibleRange.end)
                    : data
                  ).map((rowData: any, sliceIndex: number) => {
                    const index = windowRows
                      ? visibleRange.start + sliceIndex
                      : sliceIndex;
                    return (
                      <ReportTableRow
                        groupNestingLevel={0}
                        rowIndex={index}
                        rawRowData={rawData[index]}
                        rowData={rowData}
                        // key moved here from <ReportTable /> to <ReportTableRow /> to reset the row on column search
                        key={`report_table_row_${index}_${columnsSearchKeywordKey}`}
                      />
                    );
                  })}
                  {windowRows && visibleRange.end < rowCount && (
                    <tr
                      aria-hidden="true"
                      style={{ height: (rowCount - visibleRange.end) * rowHeight }}
                    />
                  )}
                </>
              ) : isLoading ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="text-center py-24 text-lg font-bold text-gray-400"
                  >
                    &nbsp;
                  </td>
                </tr>
              ) : (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="text-center text-lg font-bold text-gray-400"
                  >
                    <div className="sticky left-0 w-report-table-no-items min-h-report-table-no-items flex items-center justify-center pointer-events-none touch-none">
                      {t("no_items_found")}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          )}
          <thead
            className={`sticky ${hoveringColumnFiltersRow ? "z-listing-table-header-icons" : "z-report-table-thead"} top-0 bg-white border-b border-gray-500 ${customTableHeadStyles}`}
          >
            <tr>
              {columns
                ?.filter((column) => {
                  if (column.id === "index") return true;
                  // use columnsVisibility to check if column is visible
                  return columnsVisibility.find(
                    (col) => col.name === column.header && col.show
                  );
                })
                ?.map((column, index) => (
                  <th
                    key={index}
                    className={`text-left text-sm font-semibold text-black leading-6 whitespace-nowrap p-0 ${listingQueryParams.ordering === column.id || listingQueryParams.ordering === `-${column.id}` ? "text-blue-500" : "text-black"}`}
                  >
                    <Droppable
                      droppableId={`${DROPPABLE_ID_PREFIX_REPORT_COLUMN}${column.id}`}
                      direction="horizontal"
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={
                            `relative flex items-center border-b-2 border-gray-400 p-1 h-12 ` +
                            `${column.ordering ? " group cursor-pointer" : ""}` +
                            `${snapshot.isDraggingOver ? " bg-red-200" : ""}`
                          }
                          title={
                            listingQueryParams?.ordering === column.id
                              ? tReports("column_header_title_reset_sorting")
                              : listingQueryParams?.ordering === `-${column.id}`
                                ? tReports(
                                    "column_header_title_ascending_order"
                                  )
                                : tReports(
                                    "column_header_title_descending_order"
                                  )
                          }
                        >
                          {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
                            <pre className="absolute top-0 left-0 text-[8px] leading-[8px] font-normal opacity-50">
                              #{column.id}{" "}
                              <span className="text-red-600 font-bold">
                                type:{column.field_output_type}
                              </span>
                              <br />#{column.all_field_id}
                              {/* <br />
                            {JSON.stringify(column, null, 2)} */}
                            </pre>
                          )}
                          <Draggable draggableId={`col_${index}`} index={index}>
                            {(provided, snapshot) => (
                              <span
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`group/column-header flex items-center gap-2 rounded-lg group-hover:bg-accent-blue-paleO66 px-4${reportType !== REPORT_TYPES.CUSTOM ? " -translate-x-1" : ""} ${index === columns.length - 1 ? "mr-24 Xpl-3 Xpr-24" : "Xpx-4"}${snapshot.isDragging ? " bg-accent-blue-pale" : ""}`}
                                onClick={() => onSortClick(column)}
                              >
                                {/* Drag Handle */}
                                {(column?.field_object?.is_group_by ||
                                  reportType !== REPORT_TYPES.CUSTOM) && (
                                  <>
                                    <span
                                      className="absolute"
                                      {...provided.dragHandleProps}
                                    >
                                      {/* faux drag handle for group by column */}
                                    </span>{" "}
                                  </>
                                )}
                                {reportType === REPORT_TYPES.CUSTOM && (
                                  <span
                                    className={`group/column-drag-handle flex items-center justify-center size-8 ${column?.field_object?.is_group_by ? "Xbg-black/5 cursor-not-allowed" : `cursor-move ${snapshot.isDragging ? "bg-accent-blue-dark" : "hover:bg-accent-blue-dark group-hover/column-header:ring-1"}`} rounded-lg -ml-2.5 ring-accent-blue-dark/20 ${index === 0 ? " hidden" : ""}`}
                                    {...(column?.field_object?.is_group_by ||
                                    reportType !== REPORT_TYPES.CUSTOM
                                      ? {}
                                      : { ...provided.dragHandleProps })}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                    title={
                                      column?.field_object?.is_group_by
                                        ? tReports("group_by_column_locked")
                                        : tReports("drag_to_group_by_column")
                                    }
                                  >
                                    <img
                                      src={
                                        column?.field_object?.is_group_by
                                          ? svgIcons.LockIcon
                                          : svgIcons.DragHandleBars
                                      }
                                      alt="Drag Handle"
                                      className={`report-column-icon ${column?.field_object?.is_group_by ? "size-4 opacity-30" : "size-5 group-hover/column-drag-handle:contrast-200"}`}
                                    />
                                  </span>
                                )}
                                {/* Column Header Text */}
                                <span className="report-column-title flex items-center gap-1 text-l font-semibold text-black leading-10">
                                  {/* Aggregation Wrapper Opening Bracket */}
                                  {!!column.field_object
                                    ?.aggregate_function && (
                                    <span>
                                      {column.field_object?.aggregate_function}
                                      {column.field_object?.operator}
                                      {column.field_object?.value}(
                                    </span>
                                  )}

                                  {/* Column Name */}
                                  <span>{column.header}</span>

                                  {/* Column Unit */}
                                  {column?.field_object?.aggregate_function !==
                                    AGGREGATE_FUNCTION.COUNT && // don't show unit for COUNT
                                    getUnitByOutputType(
                                      column.field_output_type
                                    ) && (
                                      <span className="font-normal text-black/50">
                                        {`(${getUnitByOutputType(column.field_output_type)})`}
                                      </span>
                                    )}

                                  {/* Aggregation Wrapper Closing Bracket */}
                                  {!!column.field_object
                                    ?.aggregate_function && <span>)</span>}
                                </span>
                                {/* Ordering Icons */}
                                {column.ordering && (
                                  <span className="flex flex-col w-4">
                                    {listingQueryParams?.ordering ===
                                    column.id ? ( // results sorted in ascending order by this column
                                      <>
                                        <img
                                          src={svgIcons.CollapseIcon}
                                          alt={column.header}
                                          className="size-4 group-hover:grayscale group-hover:opacity-15"
                                        />
                                        <img
                                          src={svgIcons.ExpandMoreIcon}
                                          alt={column.header}
                                          className="size-4 grayscale opacity-50 group-hover:grayscale group-hover:opacity-15"
                                        />
                                      </>
                                    ) : listingQueryParams.ordering ===
                                      `-${column.id}` ? ( // results sorted in descending order by this column
                                      <>
                                        <img
                                          src={svgIcons.CollapseIcon}
                                          alt={column.header}
                                          className="size-4 grayscale opacity-50 group-hover:grayscale-0 group-hover:opacity-100"
                                        />
                                        <img
                                          src={svgIcons.ExpandMoreIcon}
                                          alt={column.header}
                                          className="size-4 group-hover:grayscale group-hover:opacity-15"
                                        />
                                      </>
                                    ) : (
                                      // results not sorted by this column
                                      <>
                                        <img
                                          src={svgIcons.CollapseIcon}
                                          alt={column.header}
                                          className="size-4 grayscale opacity-50 group-hover:opacity-15"
                                        />
                                        <img
                                          src={svgIcons.ExpandMoreIcon}
                                          alt={column.header}
                                          className="size-4 grayscale opacity-50 group-hover:grayscale-0 group-hover:opacity-100"
                                        />
                                      </>
                                    )}
                                  </span>
                                )}
                              </span>
                            )}
                          </Draggable>
                        </div>
                      )}
                    </Droppable>
                  </th>
                ))}
            </tr>

            {/* Column Filters */}
            {reportType !== REPORT_TYPES.STANDARD && (
              <tr
                // toggles display of icons in the header row
                onMouseOver={() => setHoveringColumnFiltersRow(true)}
                onMouseOut={() => setHoveringColumnFiltersRow(false)}
              >
                {columns
                  ?.filter((column) => {
                    if (column.id === "index") return true;
                    // use columnsVisibility to check if column is visible
                    return columnsVisibility.find(
                      (col) => col.name === column.header && col.show
                    );
                  })
                  ?.map((column, index) => {
                    const columnRef =
                      React.createRef<HTMLTableHeaderCellElement>();
                    const thisColumnSearchKeyword =
                      columnsSearchKeyword?.[String(column.id)] || "";
                    return (
                      <th key={index} ref={columnRef}>
                        <div
                          className={`relative min-w-48 font-normal flex px-4 py-2 ${!index ? "hidden" : ""}`}
                        >
                          {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
                            <span className="absolute -ml-6 text-[8px] text-left leading-[8px] z-20">
                              <span
                                className={
                                  parseInt(
                                    column.field_output_type as any as string
                                  ) == 1
                                    ? ""
                                    : "text-field-error-dark"
                                }
                              >
                                field_output_type: {column.field_output_type}
                              </span>
                              <br />
                              identifier: {column.identifier}
                              <br />
                              <span
                                className={
                                  column.show_selectable_values
                                    ? ""
                                    : "text-field-error-dark"
                                }
                              >
                                drop:{" "}
                                {column.show_selectable_values
                                  ? "true"
                                  : "false"}
                              </span>
                              <br />
                            </span>
                          )}
                          <div
                            className={`flex-grow${showBetweenDatePopupForColumn === column.id ? "" : " cursor-pointer"}${showColumnsPopup[String(index)] ? " Xz-popup" : ""}`}
                            onClick={() => {
                              // having a click event here does not make sense
                              // setShowColumnsPopup((prev) => ({
                              //   ...prev,
                              //   [String(index)]: !prev[String(index)]
                              // }));
                            }}
                          >
                            {[
                              REPORT_FIELD_OUTPUT_TYPES.DATE_TIME,
                              REPORT_FIELD_OUTPUT_TYPES.DATE,
                              REPORT_FIELD_OUTPUT_TYPES.TIME,
                              REPORT_FIELD_OUTPUT_TYPES.GREGORIAN_DATE,
                            ].includes(
                              column.field_output_type as any as REPORT_FIELD_OUTPUT_TYPES
                            ) ? (
                              <>
                                {/* Date/Time Picker */}
                                {column.id &&
                                columnsOperator[column.id] ===
                                  REPORT_FILTER_OPERATOR_TYPES.BETWEEN ? (
                                  <>
                                    {/* Between Condition */}
                                    <div className="flex items-center">
                                      {/* Select Range Button */}
                                      <AdminFormFieldSubmit
                                        type="button"
                                        onClick={() => {
                                          setShowBetweenDatePopupForColumn(
                                            column.id as string
                                          );
                                        }}
                                        variant="primary-alt"
                                        customWrapperClass={`flex-grow`}
                                        buttonDimensionClass="h-10 w-full min-w-28 px-0 rounded-tr-none rounded-br-none whitespace-nowrap"
                                        label={
                                          <span className="text-xs">
                                            {tReports(
                                              "columnFilters.select_range"
                                            )}
                                          </span>
                                        }
                                      />
                                      {/* Clear Button */}
                                      {!!thisColumnSearchKeyword &&
                                        thisColumnSearchKeyword.indexOf(",") >
                                          -1 && (
                                          <button
                                            className="bg-yellow-100 h-10 w-9 flex items-center justify-center border-t border-b border-gray-300"
                                            title="Clear"
                                            onClick={() => {
                                              debouncedSetColumnsSearchKeyword(
                                                "",
                                                String(column.id)
                                              );
                                            }}
                                          >
                                            <img
                                              src={svgIcons.CloseIcon}
                                              alt="clear"
                                              className="size-4"
                                            />
                                          </button>
                                        )}
                                    </div>
                                    {showBetweenDatePopupForColumn ===
                                      column.id &&
                                      BetweenDatePopup(column, index)}
                                  </>
                                ) : (
                                  <span className="relative">
                                    {/* Date/Time Picker */}
                                    <AdminFormFieldInput
                                      customWrapperClass="date-picker z-10"
                                      ref={(datepickerRef: any) => {
                                        if (datepickerRef?.target) {
                                          inputRefs.current[index] =
                                            datepickerRef.target as HTMLInputElement | null;
                                          datepickerRefs.current[index] =
                                            datepickerRef;
                                        } else {
                                          inputRefs.current[index] = null;
                                          datepickerRefs.current[index] = null;
                                        }
                                      }}
                                      size="md"
                                      innerWrapperClass="mt-0 relative flex w-full"
                                      // basicInputClass={`w-full ${column.field_output_type === REPORT_FIELD_OUTPUT_TYPES.DATE_TIME ? "min-w-[200px]" : ""} h-10 rounded-tl-md rounded-bl-md text-xs shadow-sm pe-10 ${thisColumnSearchKeyword.length > 0 ? "bg-yellow-100" : "bg-white"}`}
                                      basicInputClass={`w-full h-9 rounded-tl-md rounded-bl-md text-xs ${thisColumnSearchKeyword.length > 0 ? "bg-yellow-100" : "bg-white"}`}
                                      calendarIconDimensionClass="hidden"
                                      label={""} // t(`${field_start_time.param}`)}
                                      type={
                                        column.field_output_type ===
                                          REPORT_FIELD_OUTPUT_TYPES.DATE ||
                                        column.field_output_type ===
                                          REPORT_FIELD_OUTPUT_TYPES.GREGORIAN_DATE
                                          ? "date-picker"
                                          : column.field_output_type ===
                                              REPORT_FIELD_OUTPUT_TYPES.TIME
                                            ? "time-picker"
                                            : "datetime-picker"
                                      }
                                      id={`datetime-picker_${index}`}
                                      name={`datetime-picker_${index}`}
                                      value={
                                        thisColumnSearchKeyword
                                          ? column.field_output_type ===
                                            REPORT_FIELD_OUTPUT_TYPES.GREGORIAN_DATE
                                            ? // gregorain date
                                              calculateGregorianDate(
                                                Number(thisColumnSearchKeyword)
                                              )
                                            : column.field_output_type ===
                                                REPORT_FIELD_OUTPUT_TYPES.DATE
                                              ? // date
                                                thisColumnSearchKeyword
                                              : column.field_output_type ===
                                                  REPORT_FIELD_OUTPUT_TYPES.TIME
                                                ? // time
                                                  `${new Date().toISOString().split("T")[0]}T${formattedDateTimeBasedOnTimezone(
                                                    fromZonedTime(
                                                      `${new Date().toISOString().split("T")[0]}T${thisColumnSearchKeyword}`,
                                                      "GMT" // tzForDateFns
                                                    ),
                                                    tzForDateFns,
                                                    APP_CONFIG.DATETIME_FORMATS
                                                      .BACKEND.TIME
                                                  )}`
                                                : // date/time
                                                  formattedDateTimeBasedOnTimezone(
                                                    dbTimeToJsTime(
                                                      parseInt(
                                                        thisColumnSearchKeyword
                                                      )
                                                    ),
                                                    tzForDateFns,
                                                    APP_CONFIG.DATETIME_FORMATS
                                                      .DATEPICKER
                                                      .OUTPUT_DATE_TIME_SEC // "yyyy-MM-dd'T'HH:mm:ss"
                                                  )
                                          : ""
                                      }
                                      enableSecondsSelect={true}
                                      // restrict only when field_output_type is not gregorian date
                                      {...(column.field_output_type !==
                                        REPORT_FIELD_OUTPUT_TYPES.GREGORIAN_DATE && {
                                        maxTime: new Date(),
                                      })}
                                      onDatepickerChange={(
                                        fieldName: string,
                                        date: any
                                      ) => {
                                        let dateValueToSave =
                                          handleDateTimePickerValueOnChange(
                                            column,
                                            tzForDateFns,
                                            date
                                          );

                                        if (
                                          column.field_output_type ===
                                            REPORT_FIELD_OUTPUT_TYPES.TIME &&
                                          dateValueToSave?.length !== 8
                                        )
                                          return;
                                        debouncedSetColumnsSearchKeyword(
                                          String(dateValueToSave),
                                          String(column.id)
                                        );
                                      }}
                                    />
                                    {/* clear button */}
                                    {thisColumnSearchKeyword && (
                                      <button
                                        className="absolute top-0 right-0 bg-yellow-100 h-9 w-9 flex items-center justify-center z-10"
                                        onClick={() => {
                                          debouncedSetColumnsSearchKeyword(
                                            "",
                                            String(column.id)
                                          );
                                        }}
                                      >
                                        <img
                                          src={svgIcons.CloseIcon}
                                          alt="clear"
                                          className="size-4"
                                        />
                                      </button>
                                    )}
                                  </span>
                                )}
                              </>
                            ) : (
                              <>
                                {[
                                  REPORT_FIELD_OUTPUT_TYPES.NUMBER,
                                  REPORT_FIELD_OUTPUT_TYPES.KPH_TO_SPEED_UNITS,
                                  REPORT_FIELD_OUTPUT_TYPES.KM_TO_DISTANCE_UNITS,
                                  REPORT_FIELD_OUTPUT_TYPES.M_TO_DISTANCE_UNITS,
                                  REPORT_FIELD_OUTPUT_TYPES.L_TO_VOLUME_UNITS,
                                  REPORT_FIELD_OUTPUT_TYPES.KPL_ECONOMY_UNITS,
                                  REPORT_FIELD_OUTPUT_TYPES.KPA_TO_PRESSURE_UNITS,
                                  REPORT_FIELD_OUTPUT_TYPES.C_TO_TEMPERATURE_UNITS,
                                ].includes(
                                  column.field_output_type as any as REPORT_FIELD_OUTPUT_TYPES
                                ) &&
                                column.id &&
                                columnsOperator[column.id] ===
                                  REPORT_FILTER_OPERATOR_TYPES.BETWEEN ? (
                                  <div className="flex">
                                    {/* Select Range Button */}
                                    <AdminFormFieldSubmit
                                      type="button"
                                      onClick={() => {
                                        setShowBetweenNumberPopupForColumn(
                                          column.id as string
                                        );
                                      }}
                                      variant="primary-alt"
                                      customWrapperClass={`flex-grow`}
                                      buttonDimensionClass="h-10 w-full min-w-28 px-0 rounded-tr-none rounded-br-none whitespace-nowrap"
                                      label={
                                        <span className="text-xs">
                                          {tReports(
                                            "columnFilters.select_range"
                                          )}
                                        </span>
                                      }
                                    />
                                    {!!thisColumnSearchKeyword &&
                                      thisColumnSearchKeyword.indexOf(",") >
                                        -1 && (
                                        <button
                                          className="bg-yellow-100 h-10 w-9 flex items-center justify-center border-t border-b border-gray-300"
                                          title="Clear"
                                          onClick={() => {
                                            debouncedSetColumnsSearchKeyword(
                                              "",
                                              String(column.id)
                                            );
                                          }}
                                        >
                                          <img
                                            src={svgIcons.CloseIcon}
                                            alt="clear"
                                            className="size-4"
                                          />
                                        </button>
                                      )}
                                    <>
                                      {showBetweenNumberPopupForColumn &&
                                        BetweenNumberPopup(column, index)}
                                    </>
                                  </div>
                                ) : (
                                  //Search Box
                                  <AppSearchBox
                                    ref={(searchBoxRef) => {
                                      if (searchBoxRef) {
                                        inputRefs.current[index] =
                                          searchBoxRef as HTMLInputElement | null;
                                      } else {
                                        inputRefs.current[index] = null;
                                      }
                                    }}
                                    wrapperClassName="w-full"
                                    inputClassName={`w-full min-w-32 ${thisColumnSearchKeyword.length > 0 ? "bg-yellow-100" : "bg-white"} rounded-tl-md rounded-bl-md enabled:outline-accent-blue-dark focus-visible:outline-4 focus-visible:shadow-none h-9 py-0 ps-10 shadow-sm sm:text-sm`}
                                    initialValue={
                                      thisColumnSearchKeyword
                                        ? convertToUnit(
                                            column?.field_output_type as REPORT_FIELD_OUTPUT_TYPES,
                                            thisColumnSearchKeyword,
                                            column?.convert_to_whole_number
                                          )?.finalData
                                        : undefined
                                    }
                                    placeholder={tReports(
                                      "columnFilters.search_placeholder"
                                    )}
                                    disabled={false}
                                    loading={false}
                                    onChange={(e) => {
                                      const searchValue = String(
                                        e.target.value
                                      );
                                      const backendValue =
                                        getBackendValueForEventSearch(
                                          searchValue,
                                          column.field_output_type
                                        );
                                      // Set operator based on search value for STATUS_WITH_COLOR
                                      if (
                                        column.field_output_type ===
                                          REPORT_FIELD_OUTPUT_TYPES.STATUS_WITH_COLOR &&
                                        column.id
                                      ) {
                                        const columnId = String(column.id);
                                        const lowerSearchValue =
                                          searchValue.toLowerCase();

                                        if (
                                          lowerSearchValue.includes("driving")
                                        ) {
                                          setColumnsOperator?.((prev) => ({
                                            ...prev,
                                            [columnId]:
                                              REPORT_FILTER_OPERATOR_TYPES.IN,
                                          }));
                                        } else if (searchValue.trim() !== "") {
                                          setColumnsOperator?.((prev) => ({
                                            ...prev,
                                            [columnId]:
                                              REPORT_FILTER_OPERATOR_TYPES.LIKE,
                                          }));
                                        }
                                      }
                                      debouncedSetColumnsSearchKeyword(
                                        String(
                                          convertValueForAPI(
                                            column.field_output_type,
                                            backendValue
                                          )
                                        ),
                                        String(column.id)
                                      );
                                    }}
                                    onClear={() => {
                                      debouncedSetColumnsSearchKeyword(
                                        "",
                                        String(column.id)
                                      );
                                    }}
                                  />
                                )}
                              </>
                            )}
                          </div>

                          {true && ( // column.show_selectable_values
                            <>
                              {/* Filter Button and Popup Wrapper */}
                              <div className={`relative flex`}>
                                {/* Filter Button */}
                                <button
                                  className={`group flex-grow w-10 flex items-center justify-center shrink-0 relative ${showColumnsPopup[String(index)] ? "bg-gray-500 z-popup" : "bg-gray-300 enabled:hover:bg-gray-500"} rounded-tr-md rounded-br-md`}
                                  onClick={() => {
                                    setShowColumnsPopup((prev) => {
                                      const newState = {
                                        ...prev,
                                        [String(index)]: !prev[String(index)],
                                      };
                                      // if column filter popup is opened, scroll to ensure it is visible
                                      if (
                                        newState[String(index)] &&
                                        wrapperRef.current &&
                                        !!columnRef.current?.offsetLeft
                                      ) {
                                        wrapperRef.current.scrollTo({
                                          behavior: "smooth",
                                          left:
                                            columnRef.current?.offsetLeft - 100,
                                        });
                                      }
                                      return newState;
                                    });
                                  }}
                                >
                                  <img
                                    className={`size-6 ${showColumnsPopup[String(index)] ? "grayscale-0 brightness-0 invert" : "group-hover:grayscale-0 group-hover:brightness-0 group-hover:invert"}`}
                                    src={svgIcons.ColFilterIcon}
                                  />
                                  {/* dot when this column filter is applied */}
                                  {!!reportFilters &&
                                    reportFilters?.some(
                                      (filter) =>
                                        filter.filter_type ===
                                          REPORT_FILTER_TYPES.COLUMN &&
                                        filter.param === column.identifier &&
                                        filter.value.length > 0
                                    ) && (
                                      <span
                                        className="absolute top-0 right-0 w-2 h-2 bg-accent-blue-dark outline outline-4 outline-accent-blue-pale rounded-full"
                                        title={t("filter_applied")}
                                      />
                                    )}
                                </button>

                                {/* Aggregation Button */}
                                {Object.values(
                                  REPORT_FIELD_OUTPUT_TYPES_WITH_AGGREGATION
                                )
                                  .filter((value) => typeof value === "number")
                                  .includes(
                                    column?.field_output_type as number
                                  ) && (
                                  <button
                                    className={`mt-1 ml-2 flex items-center justify-center shrink-0 relative group ${column?.aggregation_applied ? "bg-accent-blue-pale" : "bg-white"} enabled:hover:bg-gray-500 rounded-full w-8 h-8`}
                                    onClick={() =>
                                      toggleSummationPopup(String(index))
                                    } // Toggle the summation popup
                                  >
                                    <img
                                      className="size-6 group-hover:grayscale-0 group-hover:brightness-0 group-hover:invert"
                                      src={svgIcons.SummationIcon}
                                    />
                                  </button>
                                )}
                                {/* Aggregation Popup */}
                                {showSummationPopups[String(index)] && (
                                  <PopupReportColumnAggregation
                                    reportId={parseInt(listingQueryParams.id)}
                                    onApplyReportColumnCalc={
                                      onApplyReportColumnCalc
                                    }
                                    fieldId={`${column.id}`}
                                    selected={reportFields?.find(
                                      (item) => String(item.id) === column.id
                                    )}
                                  />
                                )}
                                {/* Column Filters Popup */}
                                {showColumnsPopup[String(index)] &&
                                  !!column.field_output_type && (
                                    <PopupReportColumnFilter
                                      column={column}
                                      showSelectableValues={
                                        !!column.show_selectable_values
                                      }
                                      fieldId={`${column.id}`}
                                      identifier={column.identifier}
                                      isLast={index >= columns.length - 2} // last two columns to show popup on right side
                                      reportId={parseInt(listingQueryParams.id)}
                                      selected={
                                        (reportFilters
                                          ?.find(
                                            (filter) =>
                                              filter.filter_type ===
                                                REPORT_FILTER_TYPES.COLUMN &&
                                              filter.param === column.identifier
                                          )
                                          ?.value.split(",") || []) as string[]
                                      }
                                      onApplyReportColumnFilter={
                                        handleApplyReportColumnFilter
                                      }
                                      handlePopup={handlePopup}
                                      index={index}
                                    />
                                  )}
                              </div>

                              {/* Popup Overlay */}
                              {showColumnsPopup[String(index)] && (
                                <div
                                  className="fixed inset-0 bg-white/70 backdrop-blur-[1px] z-popup-overlay"
                                  onClick={() =>
                                    setShowColumnsPopup((prev) => ({
                                      ...prev,
                                      [String(index)]: false,
                                    }))
                                  }
                                ></div>
                              )}
                              {/* Summation Popup Overlay */}
                              {showSummationPopups[String(index)] && (
                                <div
                                  className="fixed inset-0 bg-white/70 backdrop-blur-[1px] z-popup-overlay"
                                  onClick={() =>
                                    setShowSummationPopups((prev) => ({
                                      ...prev,
                                      [String(index)]: false,
                                    }))
                                  }
                                ></div>
                              )}
                            </>
                          )}
                        </div>
                      </th>
                    );
                  })}
              </tr>
            )}
          </thead>
          {/* tbody moved before thead above - to ensure column filter popup overlapping works as expected */}
        </table>
      </div>
      </ReportColorModeContext.Provider>
      {/* pagination moved inside tfoot before thead above - to ensure column filter popup overlapping works as expected */}
    </>
  );
};

export default ReportTable;
